import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { useSidecarMeta } from '@/hooks/useSidecarMeta';

function formatDurationSeconds(seconds: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '—';
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function safeEncodeURIComponent(v: string): string {
  try {
    return encodeURIComponent(v);
  } catch {
    return v;
  }
}

export default function OutcomeInlineAudioPlayer({ gsUri }: { gsUri: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Sidecar metadata: progressive enhancement (gracefully null for old artifacts).
  const { data: sidecar } = useSidecarMeta(gsUri);

  function tryPlay(el: HTMLAudioElement) {
    try {
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err: unknown) => {
          const name = (err as { name?: string } | null)?.name;
          if (name !== 'AbortError') {
            try { console.warn('[OutcomeInlineAudioPlayer] play() failed', err); } catch {}
          }
        });
      }
    } catch {}
  }

  const src = useMemo(() => {
    const u = String(gsUri || '').trim();
    if (!u) return '';
    return `/bff/gcs/stream?gsUri=${safeEncodeURIComponent(u)}`;
  }, [gsUri]);

  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [looping, setLooping] = useState(false);
  const loopingRef = useRef(false);
  useEffect(() => { loopingRef.current = looping; }, [looping]);
  const tapTimesRef = useRef<number[]>([]);
  const [duration, setDuration] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [hasError, setHasError] = useState(false);

  // Pre-set duration from sidecar if <audio> metadata hasn't loaded yet.
  useEffect(() => {
    if (duration == null && sidecar?.duration_ms != null && sidecar.duration_ms > 0) {
      setDuration(sidecar.duration_ms / 1000);
    }
  }, [sidecar, duration]);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const copyTimerRef = useRef<number | null>(null);

  const remainingLabel = useMemo(() => {
    if (!duration || !Number.isFinite(duration)) return '—';
    const rem = Math.max(0, duration - currentTime);
    return `-${formatDurationSeconds(rem)}`;
  }, [duration, currentTime]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    setCopyState('idle');
    if (copyTimerRef.current != null) {
      window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = null;
    }

    setIsReady(false);
    setIsPlaying(false);
    setLooping(false);
    loopingRef.current = false;
    tapTimesRef.current = [];
    setDuration(null);
    setCurrentTime(0);
    setHasError(false);

    const onLoadedMetadata = () => {
      const d = Number.isFinite(el.duration) ? el.duration : NaN;
      setDuration(Number.isFinite(d) ? d : null);
      setIsReady(true);
    };
    const onDurationChange = () => {
      const d = Number.isFinite(el.duration) ? el.duration : NaN;
      setDuration(Number.isFinite(d) ? d : null);
    };
    const onTimeUpdate = () => {
      setCurrentTime(el.currentTime || 0);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => {
      setIsPlaying(false);
      if (!loopingRef.current) {
        setLooping(false);
      }
    };
    const onEnded = () => {
      if (loopingRef.current) {
        try { el.currentTime = 0; } catch {}
        tryPlay(el);
        return;
      }
      setIsPlaying(false);
      setLooping(false);
      tapTimesRef.current = [];
    };
    const onError = () => {
      setHasError(true);
      setIsReady(false);
      setIsPlaying(false);
      setDuration(null);
    };

    el.addEventListener('loadedmetadata', onLoadedMetadata);
    el.addEventListener('durationchange', onDurationChange);
    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('error', onError);

    return () => {
      el.removeEventListener('loadedmetadata', onLoadedMetadata);
      el.removeEventListener('durationchange', onDurationChange);
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('error', onError);
    };
  }, [src]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current != null) window.clearTimeout(copyTimerRef.current);
    };
  }, []);

  async function copyToClipboard(text: string) {
    const s = String(text ?? '').trim();
    if (!s) return;

    function bump(next: 'copied' | 'error') {
      setCopyState(next);
      if (copyTimerRef.current != null) window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => {
        setCopyState('idle');
        copyTimerRef.current = null;
      }, 1300);
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(s);
        bump('copied');
        return;
      }

      const el = document.createElement('textarea');
      el.value = s;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      el.style.top = '0';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      bump(ok ? 'copied' : 'error');
    } catch {
      bump('error');
    }
  }

  const copyLabel = copyState === 'copied' ? 'Copied' : (copyState === 'error' ? 'Copy failed' : 'Copy');
  const copyClassName =
    'h-6 px-2 rounded border bg-transparent hover:border-neutral-500 text-[11px] leading-none whitespace-nowrap '
    + (copyState === 'copied'
      ? 'text-emerald-300 border-emerald-900/40'
      : (copyState === 'error'
        ? 'text-rose-300 border-rose-900/40'
        : 'text-neutral-400 border-neutral-800'));

  function togglePlay() {
    const el = audioRef.current;
    if (!el) return;

    if (looping) {
      el.pause();
      try { el.currentTime = 0; } catch {}
      setCurrentTime(0);
      setIsPlaying(false);
      setLooping(false);
      tapTimesRef.current = [];
      return;
    }

    const now = Date.now();
    tapTimesRef.current = tapTimesRef.current.filter((t) => now - t < 500);
    tapTimesRef.current.push(now);

    if (tapTimesRef.current.length >= 3) {
      tapTimesRef.current = [];
      try { el.currentTime = 0; } catch {}
      setCurrentTime(0);
      tryPlay(el);
      setLooping(true);
      return;
    }

    if (el.paused) {
      try { el.currentTime = 0; } catch {}
      setCurrentTime(0);
      tryPlay(el);
    } else {
      el.pause();
      try { el.currentTime = 0; } catch {}
      setCurrentTime(0);
    }
  }

  function onSeek(v: string) {
    const el = audioRef.current;
    if (!el) return;
    const n = Number(v);
    if (!Number.isFinite(n)) return;
    el.currentTime = Math.max(0, Math.min(duration ?? n, n));
  }

  const max = Number.isFinite(duration ?? NaN) && (duration ?? 0) > 0 ? duration! : 0;

  return (
    <div className="w-full" title={gsUri}>
      <div className="flex items-center gap-2 w-full">
        <button
          type="button"
          className="h-6 w-6 rounded border border-neutral-800 bg-transparent text-neutral-300 hover:border-neutral-500 text-[11px] leading-none"
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
          disabled={!src || hasError}
        >
          {isPlaying ? <Pause className="h-3.5 w-3.5 mx-auto" /> : <Play className="h-3.5 w-3.5 mx-auto" />}
        </button>

        <input
          type="range"
          className="flex-1 h-1 accent-neutral-500"
          min={0}
          max={max}
          step={0.01}
          value={Math.min(max || 0, currentTime)}
          onChange={(e) => onSeek(e.target.value)}
          aria-label="Seek audio"
          disabled={!isReady || !max || hasError}
        />

        <div className="text-[11px] font-mono text-neutral-500 whitespace-nowrap tabular-nums">{remainingLabel}</div>

        <button
          type="button"
          className={copyClassName}
          onClick={() => void copyToClipboard(gsUri)}
          aria-label="Copy audio artifact URI"
          disabled={!gsUri}
        >
          {copyLabel}
        </button>

        <audio ref={audioRef} preload="metadata" src={src} />
      </div>
    </div>
  );
}
